import { useState, useEffect } from 'react';
import { api } from '../../api';
import type { OnboardingStatus, SchemaDeliveryMode } from '../../types';
import { OnboardingWizard } from './OnboardingWizard';

interface Props {
  domainId: string;
  tier: string;
  onBack: () => void;
}

export function OnboardingPage({ domainId, tier, onBack }: Props) {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [domain, setDomain] = useState<string>('');
  const [schemaDeliveryMode, setSchemaDeliveryMode] = useState<SchemaDeliveryMode>('undecided');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      api.onboarding.getStatus(domainId),
      api.domains.get(domainId),
    ])
      .then(([s, d]) => {
        setStatus(s);
        setDomain(d.domain);
        setSchemaDeliveryMode(d.schemaDeliveryMode ?? 'undecided');
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load onboarding status'))
      .finally(() => setLoading(false));
  }, [domainId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-paper flex items-center justify-center">
        <div className="w-6 h-6 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !status) {
    return (
      <div className="min-h-screen bg-brand-paper flex flex-col items-center justify-center gap-4">
        <p className="text-red-500 text-sm">{error || 'Failed to load onboarding'}</p>
        <button onClick={onBack} className="text-sm text-brand-green hover:underline">
          ← Back to dashboard
        </button>
      </div>
    );
  }

  return (
    <OnboardingWizard
      domainId={domainId}
      domain={domain}
      tier={tier}
      initialStatus={status}
      schemaDeliveryMode={schemaDeliveryMode}
      onBack={onBack}
    />
  );
}
